import { pickT } from "@repo/shared";
import type { FlowDocumentUploadResponse } from "@repo/shared/types";
import { FileText } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { postFormData } from "@/lib/api/_client";
import { T } from "./file-editor.i18n";

// Allowed document types — mirrors the server-side allowlist in upload-service.ts
const ACCEPTED_EXTENSIONS = ".pdf,.doc,.docx,.xls,.xlsx,.ods,.csv,.txt";
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.spreadsheet",
  "text/csv",
  "text/plain",
];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

type FileEditorProps = {
  label: string;
  fileName: string;
  flowLanguage: string;
  onLabelChange: (value: string) => void;
  onFileChange: (s3Key: string, fileName: string) => void;
};

export function FileEditor({
  label,
  fileName,
  flowLanguage,
  onLabelChange,
  onFileChange,
}: FileEditorProps) {
  const [uploading, setUploading] = useState(false);
  const t = pickT(T, flowLanguage);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      alert(t.invalidFileType);
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      alert(t.fileTooLarge);
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const data = await postFormData<FlowDocumentUploadResponse>(
        "/internal/uploads/upload-flow-document",
        formData,
      );
      onFileChange(data.s3Key, data.fileName);
    } catch (_error) {
      alert(t.uploadFailed);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="file-upload">
          {t.document} <span className="text-destructive">*</span>
        </Label>
        <div className="flex gap-2">
          <Input
            id="file-upload"
            type="file"
            accept={ACCEPTED_EXTENSIONS}
            onChange={handleFileChange}
            disabled={uploading}
          />
          {uploading && (
            <Button disabled size="sm">
              {t.uploading}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{t.helpText}</p>
      </div>

      {fileName && (
        <div className="flex items-center gap-2 text-sm border rounded-lg p-3 bg-muted/50">
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-muted-foreground">{t.currentFile}:</span>
          <span className="font-medium truncate">{fileName}</span>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="file-link-label">{t.linkLabel}</Label>
        <Input
          id="file-link-label"
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder={t.linkLabelPlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.linkLabelHint}</p>
      </div>
    </div>
  );
}
